/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";
import { useRouter } from "next/router";
import { useAuthContext } from "@/context/auth/AuthContext";
import { encodeForURL } from "@/context/utilities";
import LoadAndDisplay from "@/context/LoadAndDisplay";
import DataNotFound from "../NotFound/DataNotFound";
import Image from "next/image";
import Link from "next/link";
import { toast } from "react-toastify";
import Pagination from "../Common/Pagination";

const Wishlist = ({ col }) => {
  const router = useRouter();
  const { user } = useAuthContext();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(6);

  // ✅ fetch data
  const fetchData = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const response = await axios.get("/api/video/bookmark", {
        params: {
          email: user.email,
          page: currentPage,
          limit,
          search,
        },
      });

      if (response.status === 200) {
        setData(response.data.data);
        setPages(response.data.pages);
        setError(null);
      } else {
        setError(
          response.data.message || "An error occurred during data fetching."
        );
      }
    } catch (err) {
      setError("Fetching data failed. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ remove bookmark
  const removeBookmark = async (id) => {
    try {
      await axios.delete("/api/video/bookmark", {
        data: { id, email: user.email },
      });
      toast.success("Removed from bookmarks");
      fetchData();
    } catch (err) {
      toast.error("Failed to remove bookmark");
    }
  };

  useEffect(() => {
    fetchData();
  }, [user, currentPage, search]);

  return (
    <div className="rbt-dashboard-content bg-color-white rbt-shadow-box">
      <div className="content">
        <div className="section-title d-flex justify-content-between align-items-center">
          <h4 className="rbt-title-style-3">Bookmarks</h4>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setCurrentPage(1);
              fetchData();
            }}
            className="d-flex align-items-center gap-2"
          >
            <input
              type="text"
              placeholder="Search bookmarks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-control"
              style={{ maxWidth: 250 }}
            />
            <button type="submit" className="rbt-btn btn-md btn-gradient">
              <i className="feather-search"></i>
            </button>
          </form>
        </div>

        <div className="row g-4 mt-3">
          <LoadAndDisplay isLoading={loading}>
            {data.length === 0 ? (
              <DataNotFound title="No Bookmarks" />
            ) : (
              data.map((slide, index) => (
                <div
                  className={col || "col-lg-4 col-md-6 col-12"}
                  key={`course-wishlist-${index}`}
                >
                  <div className="card rbt-card h-100 shadow-sm">
                    {/* Thumbnail */}
                    <div className="card-img-top position-relative">
                      {slide?.thumbnail?.[0]?.Location && (
                        <Image
                          src={
                            slide?.thumbnail?.[0]?.Location ||
                            "/public/images/testimonial/lawyer-client-meeting.jpg"
                          }
                          alt={slide.displayName}
                          width={400}
                          height={200}
                          className="w-100 rounded-top"
                        />
                      )}
                    </div>

                    {/* Body */}
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title">{slide.displayName}</h5>
                      <p className="card-text text-muted mb-2">
                        {slide.notes?.length > 0 ? (
                          <a
                            href={slide.notes[0].Location}
                            target="_blank"
                            rel="noreferrer"
                            className="text-decoration-underline"
                          >
                            Notes available
                          </a>
                        ) : (
                          "No notes"
                        )}
                      </p>

                      <small className="text-muted mb-3">
                        Added: {new Date(slide.createdAt).toLocaleDateString()}
                      </small>

                      {/* Actions */}
                      <div className="mt-auto d-flex justify-start">
                        <button
                          onClick={() => {
                            router.push(
                              `/student/library/${slide.subject.$id}/${
                                slide.topic.$id
                              }/${encodeForURL(slide.displayName)}-${
                                slide._id
                              }/watch`
                            );
                          }}
                          className="rbt-btn btn-xs radius-round bg-primary me-1"
                        >
                          <i className="feather-eye me-1"></i>
                        </button>
                        <button
                          onClick={() => removeBookmark(slide._id)}
                          className="rbt-btn btn-xs radius-round bg-danger"
                        >
                          <i className="feather-trash-2 me-1"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </LoadAndDisplay>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="mt-4">
            <Pagination
              totalPages={pages}
              pageNumber={currentPage}
              handleClick={setCurrentPage}
              maxItems={limit}
              setMaxItems={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
